import React, { ChangeEvent, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import EmailMultiSelect from '../../../../../../shared/EmailMultiSelect';
import { FormControl, FormGroup, Button } from 'react-bootstrap';
import { useDocumentState } from '../../../../../../../contexts/DocumentContext';
import { useApolloClient } from '@apollo/client';
import * as yup from 'yup';
import { Form, Formik } from 'formik';
import { useAuth } from '../../../../../../../auth';
import SubmitButton from '../../../../../../submit-button';
import { useNotifications } from '../../../../../../../notifications';
import { OptionalMessageInputRef } from '../../../../../document-wizard-modal/custom-modals/document-wizard-action-modal/OptionalMessageInputRef';
import { shareDocumentByEmail } from '../../../../../../../utils/api/share-document-by-email';
import { EMAIL_VALIDATION_REGEX } from '../../../../../../../shared/constants/emailValidation';
import { TextField } from '../../../../../../fields';
import styles from '../RequestReview.module.scss';
import classNames from 'classnames';
import { Checkbox, Radio } from '../../../../../../checkbox-or-radio-field';

const DOC_TYPE_OPTIONS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'docx', label: 'DocX' },
];

const inlineSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    fontSize: '14px',
    minHeight: '32px',
    borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(33, 150, 243, 0.1)' : null,
    '&:hover': {
      borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    },
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    padding: '4px',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 10000,
    minWidth: '100px',
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  option: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: '0 8px',
  }),
};

interface ExtraAttachment {
  id: string;
  order: number;
  attachment?: {
    filename: string;
  };
}

interface RequestReviewViaEmailFormProps {
  bodyContainer: React.ComponentType | keyof React.ReactHTML;
  actionsContainer: React.ComponentType | keyof React.ReactHTML;
  renderSecondaryAction: () => {};
  onSuccess: () => void;
  teamId: string;
  dealId: string;
  enableAttachments: boolean;
  disableWordExport?: boolean;
  extraAttachments?: ExtraAttachment[];
  documentSource?: string;
}

const createValidationSchema = (currentUserEmail: string | undefined) =>
  yup.object().shape({
    email: yup
      .string()
      .trim()
      .required('Email is required')
      .matches(EMAIL_VALIDATION_REGEX, 'Please provide correct email')
      .test(
        'not-self-email',
        'You cannot send a document to yourself',
        (value) => !currentUserEmail || value?.toLowerCase() !== currentUserEmail.toLowerCase()
      ),
  });

const RequestReviewViaEmailForm = ({
  bodyContainer: BodyContainer = React.Fragment,
  actionsContainer: ActionsContainer = React.Fragment,
  renderSecondaryAction,
  onSuccess,
  teamId: _teamId,
  dealId: _dealId,
  enableAttachments,
  disableWordExport = false,
  extraAttachments = [],
  documentSource,
}: RequestReviewViaEmailFormProps) => {
  const apolloClient = useApolloClient();
  const [{ user }] = useAuth();
  const currentUserEmail = user?.email;

  // Memoize validation schema with current user's email to prevent self-sending
  const validationSchema = useMemo(
    () => createValidationSchema(currentUserEmail),
    [currentUserEmail]
  );

  const [ccEmails, setCCEmails] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSubject, setEmailSubject] = useState<string | null>(null);
  const [includeAttachments, setIncludeAttachments] = useState<boolean>(enableAttachments);
  const [sendInvitationButtonDisabled, setSendInvitationButtonDisabled] = useState(true);
  const [documentType, setDocumentType] = useState<'pdf' | 'docx'>('pdf');
  const [mergeAttachments, setMergeAttachments] = useState<boolean>(true);
  // Initialize with all attachment IDs selected by default
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<string[]>(
    extraAttachments.map((att) => att.id)
  );
  const { state } = useDocumentState();
  const documentTitle = state.title;
  const documentId = state.id;
  const optionalMessageInputRef = React.useRef<HTMLTextAreaElement>(null);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const placeholderMessage = `${documentTitle} - please review`;
  const includeAttachmentsRef = useRef(includeAttachments);

  // Check if export plugins are available and template allows DocX export
  // `isExportPluginAvailable` was a CKEditor-era stub that returned true.
  const isDocxExportAvailable = !disableWordExport;

  useEffect(() => {
    setSendInvitationButtonDisabled(false);
  }, []);

  useEffect(() => {
    includeAttachmentsRef.current = includeAttachments;
  }, [includeAttachments]);

  // Sync selected attachment IDs when extraAttachments changes
  useEffect(() => {
    setSelectedAttachmentIds(extraAttachments.map((att) => att.id));
  }, [extraAttachments]);

  // Reset to PDF if DocX is not available and currently selected
  useEffect(() => {
    if (documentType === 'docx' && !isDocxExportAvailable) {
      setDocumentType('pdf');
    }
  }, [documentType, isDocxExportAvailable]);

  // Handle attachment enabling based on document type
  // PDF: Current logic (size check on combined PDF)
  // DocX: Always enable since we're not merging PDFs into DocX, just attaching separately
  const shouldEnableAttachments = documentType === 'docx' ? true : enableAttachments;

  // Auto-enable attachments when switching to DocX (if previously disabled due to size)
  useEffect(() => {
    if (documentType === 'docx' && !includeAttachments && !enableAttachments) {
      // If we're switching to DocX and attachments were disabled due to PDF size limit,
      // automatically enable them since DocX doesn't have the same size constraints
      setIncludeAttachments(true);
    }
  }, [documentType, enableAttachments, includeAttachments]);

  const onSubmitClick = useCallback(
    async (values) => {
      const additionalMessage = optionalMessageInputRef.current?.value;

      setIsSubmitting(true);

      try {
        // DOCX generation is handled server-side and attached to the email

        const variables = {
          documentId,
          ccEmails,
          toEmail: values?.email,
          subject: !!emailSubject?.trim() ? emailSubject : placeholderMessage,
          additionalMessage,
          includeAttachments: includeAttachmentsRef.current,
          confirm: true,
          documentType: documentType,
          mergeAttachments: mergeAttachments, // Only applies to PDF format
          selectedAttachmentIds: selectedAttachmentIds.length > 0 ? selectedAttachmentIds : undefined,
        };

        await shareDocumentByEmail(variables)(apolloClient);

        notifySuccess({
          message: {
            id: 'phrase.invitation.success',
            defaultMessage: '{documentTitle} has been sent to {email}',
            values: {
              email: values?.email,
              documentTitle,
            },
          },
        });

        onSuccess();
      } catch (error) {
        notifyError({ message: error instanceof Error ? error.message : 'An error occurred while sending the email' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      apolloClient,
      ccEmails,
      documentId,
      documentTitle,
      documentType,
      emailSubject,
      isDocxExportAvailable,
      mergeAttachments,
      notifyError,
      notifySuccess,
      onSuccess,
      placeholderMessage,
      selectedAttachmentIds,
    ]
  );

  const handleSend = useCallback(
    async (values) => {
      return onSubmitClick(values);
    },
    [onSubmitClick]
  );

  const handleIncludeAttachmentsChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIncludeAttachments(e.currentTarget.checked);
  };

  const handleDocumentTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as 'pdf' | 'docx';
    setDocumentType(newType);
  };

  const handleAttachmentSelection = (attachmentId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedAttachmentIds((prev) => [...prev, attachmentId]);
    } else {
      setSelectedAttachmentIds((prev) => prev.filter((id) => id !== attachmentId));
    }
  };

  const handleSelectAllAttachments = () => {
    setSelectedAttachmentIds(extraAttachments.map((att) => att.id));
  };

  const handleDeselectAllAttachments = () => {
    setSelectedAttachmentIds([]);
  };

  return (
    <Formik
      validateOnBlur
      validateOnChange
      initialValues={{
        email: '',
      }}
      onSubmit={(values) => {
        handleSend(values);
      }}
      validationSchema={validationSchema}
    >
      {({ isValid, errors, touched }) => {
        return (
          <Form>
            <BodyContainer>
              <p>
                <FormattedMessage
                  id="phrase.invite-team-member.instructions"
                  defaultMessage="Responses to this email will appear in the Deal Activity feed"
                />
              </p>

              <div className={styles.emailInputField}>
                <TextField
                  id="inviteReceivingTeamForm.email"
                  name="email"
                  type="email"
                  label="To:"
                />
                {touched.email && errors.email && (
                  <div className="text-danger small mt-1">{errors.email}</div>
                )}
              </div>

              <label
                className="text-muted font-weight-bold form-label mt-1"
                htmlFor="requestReviewViaEmailForm.emailCC"
              >
                CC:
              </label>
              <EmailMultiSelect
                emails={ccEmails}
                onChange={(_emails) => {
                  setCCEmails(_emails);
                }}
                getLabel={(email, index, removeEmail) => {
                  return (
                    <div data-tag key={index}>
                      {email}
                      <span data-tag-handle onClick={() => removeEmail(index)}>
                        ×
                      </span>
                    </div>
                  );
                }}
              />
              <label
                className="text-muted font-weight-bold form-label mt-1"
                htmlFor="requestReviewViaEmailForm.emailTo"
              >
                Subject:
              </label>
              <FormControl
                id="requestReviewViaEmailForm.subject"
                data-testid="requestReviewViaEmailForm.subject-field"
                name="requestReviewViaEmailForm.subject"
                type="text"
                placeholder={placeholderMessage}
                value={emailSubject || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setEmailSubject(e.currentTarget.value)}
                onFocus={(evt: any) => {
                  evt.currentTarget.placeholder = '';
                }}
                onBlur={(evt: any) => {
                  evt.currentTarget.placeholder = placeholderMessage;
                }}
              ></FormControl>
              <OptionalMessageInputRef ref={optionalMessageInputRef} />
              <div className="mt-2 d-flex align-items-center">
                <div className="text-muted mr-2">&#128206; {`${documentTitle}.${documentType}`}</div>
                {isDocxExportAvailable && (
                  <div className="ml-2" style={{ width: '110px', display: 'inline-block' }}>
                    <Select
                      options={DOC_TYPE_OPTIONS}
                      value={DOC_TYPE_OPTIONS.find((o) => o.value === documentType) || DOC_TYPE_OPTIONS[0]}
                      onChange={(option: any) => setDocumentType(option ? option.value : 'pdf')}
                      isSearchable={false}
                      styles={inlineSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuPlacement="auto"
                    />
                  </div>
                )}
              </div>

              {/* Hide attachments section for imported PDF documents (single PDF, no attachments concept) */}
              {documentSource !== 'imported' && (
                <>
                  <Checkbox
                    id="requestReviewViaEmailForm.attachments"
                    data-testid="requestReviewViaEmailForm.attachments-field"
                    name="requestReviewViaEmailForm.attachments"
                    checked={includeAttachments && shouldEnableAttachments}
                    onChange={handleIncludeAttachmentsChange}
                    disabled={!shouldEnableAttachments}
                    wrapperClassName={classNames(styles.includeAttachmentsFormGroup, 'mt-3')}
                    label={<span className="text-muted font-weight-light">Include document attachments</span>}
                  />

                  {/* Merge/Separate attachments toggle - only for PDF format with attachments enabled */}
                  {documentType === 'pdf' && includeAttachments && shouldEnableAttachments && (
                    <div className="mt-2 ml-4 d-flex align-items-center">
                      <Radio
                        id="mergeAttachments-merge"
                        name="mergeAttachmentsOption"
                        checked={mergeAttachments}
                        onChange={() => setMergeAttachments(true)}
                        wrapperClassName="mr-3 mb-0"
                        label={<span className="text-muted font-weight-light">Merge PDFs</span>}
                      />
                      <Radio
                        id="mergeAttachments-separate"
                        name="mergeAttachmentsOption"
                        checked={!mergeAttachments}
                        onChange={() => setMergeAttachments(false)}
                        wrapperClassName="mb-0"
                        label={<span className="text-muted font-weight-light">Send Separately</span>}
                      />
                    </div>
                  )}

                  {/* Attachment checkbox selection - show when attachments are enabled and there are attachments */}
                  {includeAttachments && shouldEnableAttachments && extraAttachments.length > 0 && (
                    <FormGroup className="mt-3 ml-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="text-muted font-weight-bold form-label mb-0">
                          Select attachments to include:
                        </label>
                        <div>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-primary p-0 mr-2"
                            onClick={handleSelectAllAttachments}
                            style={{ fontSize: '0.875rem' }}
                          >
                            Select All
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-secondary p-0"
                            onClick={handleDeselectAllAttachments}
                            style={{ fontSize: '0.875rem' }}
                          >
                            Deselect All
                          </Button>
                        </div>
                      </div>
                      <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px' }}>
                        {extraAttachments.map((attachment) => (
                          <Checkbox
                            key={attachment.id}
                            id={`attachment-${attachment.id}`}
                            name={`attachment-${attachment.id}`}
                            checked={selectedAttachmentIds.includes(attachment.id)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              handleAttachmentSelection(attachment.id, e.target.checked);
                            }}
                            wrapperClassName="mb-1"
                            label={
                              <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                                📎 {attachment.attachment?.filename || `Attachment ${attachment.order + 1}`}
                              </span>
                            }
                          />
                        ))}
                      </div>
                      <div className="mt-2 text-muted" style={{ fontSize: '0.75rem' }}>
                        {selectedAttachmentIds.length} of {extraAttachments.length} attachment(s) selected
                      </div>
                    </FormGroup>
                  )}
                </>
              )}
            </BodyContainer>
            <ActionsContainer>
              <>
                {renderSecondaryAction?.()}
                <SubmitButton
                  label={<FormattedMessage id="cta.send-invitation" defaultMessage="Send" />}
                  disabled={isSubmitting || sendInvitationButtonDisabled || !isValid}
                  isSubmitting={isSubmitting}
                />
              </>
            </ActionsContainer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default RequestReviewViaEmailForm;
